import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  FormGroup,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Container,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Send as SendIcon,
  CalendarMonth as CalendarIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  ArrowForward,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { employeeApi } from '../../api/employeeApi'
import { leaveApi } from '../../api/leaveApi'
import { holidayApi } from '../../api/holidayApi'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import toast from 'react-hot-toast'
import { useLoading } from '../../hooks'
import { formatDate } from '../../utils/formatters'

const ApplyLeave = () => {

  const navigate = useNavigate()
  const { user } = useSelector((state) => state.auth)
  const { loading, withLoading } = useLoading()
  const [activeStep, setActiveStep] = useState(0)
  const [formData, setFormData] = useState({
    leaveType: '',
    fromDate: null,
    toDate: null,
    fromTime: null,
    toTime: null,
    durationType: 'full_day', // full_day, half_day, hours
    totalDays: 0,
    totalHours: 0,
    reason: '',
    emergencyContact: '',
    handoverTo: '',
    workHandoverNotes: '',
    isMedical: false,
    medicalCertificate: null,
    otherDocuments: [],
    notifyManager: true,
    notifyTeam: true,
    notifyHR: true,
  })

  const [errors, setErrors] = useState({})
  const [leaveBalance, setLeaveBalance] = useState({
    casual: { total: 0, taken: 0, remaining: 0 },
    sick: { total: 0, taken: 0, remaining: 0 },
    paid: { total: 0, taken: 0, remaining: 0 },
    maternity: { total: 0, taken: 0, remaining: 0 },
    paternity: { total: 0, taken: 0, remaining: 0 },
    unpaid: { total: 0, taken: 0, remaining: 0 },
    compensatory: { total: 0, taken: 0, remaining: 0 },
  })

  const fetchLeaveBalance = async () => {
    try {
      const response = await leaveApi.getLeaveBalance(user.id)
      const balanceData = response.data

      setLeaveBalance({
        casual: {
          total: balanceData.casual_leaves?.total || 0,
          taken: balanceData.casual_leaves?.used || 0,
          remaining: balanceData.casual_leaves?.available || 0
        },
        sick: {
          total: balanceData.sick_leaves?.total || 0,
          taken: balanceData.sick_leaves?.used || 0,
          remaining: balanceData.sick_leaves?.available || 0
        },
        paid: {
          total: balanceData.paid_leaves?.total || 0,
          taken: balanceData.paid_leaves?.used || 0,
          remaining: balanceData.paid_leaves?.available || 0
        },
        maternity: {
          total: balanceData.maternity_leaves?.total || 0,
          taken: balanceData.maternity_leaves?.used || 0,
          remaining: balanceData.maternity_leaves?.available || 0
        },
        paternity: {
          total: balanceData.paternity_leaves?.total || 0,
          taken: balanceData.paternity_leaves?.used || 0,
          remaining: balanceData.paternity_leaves?.available || 0
        },
        unpaid: {
          total: balanceData.unpaid_leaves?.total || 0,
          taken: balanceData.unpaid_leaves?.used || 0,
          remaining: balanceData.unpaid_leaves?.available || 0
        },
        compensatory: {
          total: balanceData.compensatory_leaves?.total || 0,
          taken: balanceData.compensatory_leaves?.used || 0,
          remaining: balanceData.compensatory_leaves?.available || 0
        },
      })
    } catch (error) {
      console.error('Failed to fetch leave balance:', error)
      // Fallback or keep initial state if API fails
    }
  }

  useEffect(() => {
    if (user?.id) {
      fetchLeaveBalance()
    }
  }, [user?.id])

  const [teamMembers, setTeamMembers] = useState([])
  const [fileUploads, setFileUploads] = useState([])
  const [upcomingHolidays, setUpcomingHolidays] = useState([])



  const leaveTypes = [
    { value: 'casual', label: 'Casual Leave', description: 'For personal reasons', icon: '🏖️' },
    { value: 'sick', label: 'Sick Leave', description: 'Medical leave with certificate', icon: '🏥' },
    { value: 'paid', label: 'Paid Leave', description: 'Accumulated paid leave', icon: '💰' },
    { value: 'maternity', label: 'Maternity Leave', description: 'For expecting mothers', icon: '👶' },
    { value: 'paternity', label: 'Paternity Leave', description: 'For new fathers', icon: '👨‍👧' },
    { value: 'compensatory', label: 'Compensatory Leave', description: 'Leave for extra work', icon: '⏱️' },
    { value: 'unpaid', label: 'Unpaid Leave', description: 'Leave without pay', icon: '💼' },
  ]

  const steps = [
    'Select Leave Type',
    'Set Duration',
    'Provide Details',
    'Review & Submit',
  ]

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await employeeApi.listEmployees()
        // Assuming response.data is the array or response.data.employees
        const employees = response.data.employees || response.data || []
        setTeamMembers(employees.map(emp => ({
          id: emp.employee_id,
          name: `${emp.first_name} ${emp.last_name}`,
          role: emp.designation
        })))
      } catch (error) {
        console.error('Failed to fetch team members:', error)
      }
    }
    fetchTeamMembers()

    // Fetch upcoming holidays
    const fetchUpcomingHolidays = async () => {
      try {
        const response = await holidayApi.getUpcomingHolidays()
        setUpcomingHolidays(response.data)
      } catch (error) {
        console.error('Failed to fetch upcoming holidays:', error)
      }
    }
    fetchUpcomingHolidays()

  }, [])

  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
      const from = new Date(formData.fromDate)
      const to = new Date(formData.toDate)
      const diffTime = Math.abs(to - from)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1

      setFormData(prev => ({
        ...prev,
        totalDays: diffDays
      }))
    }
  }, [formData.fromDate, formData.toDate])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleFileUpload = (event, type) => {
    const files = Array.from(event.target.files)
    if (type === 'medical') {
      setFormData(prev => ({ ...prev, medicalCertificate: files[0] }))
    } else {
      setFileUploads(prev => [...prev, ...files])
    }
  }

  const handleRemoveFile = (index, type) => {
    if (type === 'medical') {
      setFormData(prev => ({ ...prev, medicalCertificate: null }))
    } else {
      setFileUploads(prev => prev.filter((_, i) => i !== index))
    }
  }

  const validateStep = (step) => {
    const newErrors = {}

    switch (step) {
      case 0:
        if (!formData.leaveType) {
          newErrors.leaveType = 'Please select a leave type'
        }
        break

      case 1:
        if (!formData.fromDate) {
          newErrors.fromDate = 'From date is required'
        }
        if (!formData.toDate) {
          newErrors.toDate = 'To date is required'
        }
        if (formData.fromDate && formData.toDate && formData.fromDate > formData.toDate) {
          newErrors.toDate = 'To date cannot be before from date'
        }
        if (formData.durationType === 'half_day' && !formData.fromTime) {
          newErrors.fromTime = 'From time is required for half day'
        }
        if (formData.durationType === 'half_day' && !formData.toTime) {
          newErrors.toTime = 'To time is required for half day'
        }
        break

      case 2:
        if (!formData.reason.trim()) {
          newErrors.reason = 'Reason is required'
        } else if (formData.reason.length < 10) {
          newErrors.reason = 'Reason should be at least 10 characters'
        }
        if (formData.leaveType === 'sick' && formData.isMedical && !formData.medicalCertificate) {
          newErrors.medicalCertificate = 'Medical certificate is required for sick leave'
        }
        if (!formData.emergencyContact) {
          newErrors.emergencyContact = 'Emergency contact number is required'
        } else if (!/^\d{10}$/.test(formData.emergencyContact)) {
          newErrors.emergencyContact = 'Please enter a valid 10-digit phone number'
        }
        break
    }

    return newErrors
  }

  const handleNext = () => {
    const stepErrors = validateStep(activeStep)
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors)
      return
    }
    setActiveStep((prevStep) => prevStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  const handleSubmit = async () => {
    withLoading(async () => {
      try {
        // Format date using local time components to avoid UTC timezone shift
        const toLocalDateString = (date) => {
          if (!date) return null
          const d = new Date(date)
          const year = d.getFullYear()
          const month = String(d.getMonth() + 1).padStart(2, '0')
          const day = String(d.getDate()).padStart(2, '0')
          return `${year}-${month}-${day}`
        }

        const payload = {
          leave_info: {
            leave_type: formData.leaveType,
            start_date: toLocalDateString(formData.fromDate),
            end_date: toLocalDateString(formData.toDate),
            duration: formData.durationType.replace('_', ' '), // "full_day" -> "full day"
            from_time: formData.fromTime ? formData.fromTime.toISOString() : null,
            to_time: formData.toTime ? formData.toTime.toISOString() : null
          },
          leave_add_info: {
            reason: formData.reason,
            emergency_contact_number: formData.emergencyContact ? Number(formData.emergencyContact) : 0, // Send 0 or valid number
            handover_to: formData.handoverTo ? Number(formData.handoverTo) : 0, // Send 0 if empty/none
            handover_notes: formData.workHandoverNotes || ""
          },
          notification_settings: {
            notify_manager: formData.notifyManager,
            notify_hr: formData.notifyHR,
            notify_team: formData.notifyTeam
          }
        }


        await employeeApi.applyLeave(payload)
        navigate('/employee/leave')
      } catch (error) {
        console.error('Failed to submit leave application:', error)
      }
    })
  }

  const handleSaveDraft = () => {
    toast.success('Leave application saved as draft')
    navigate('/employee/leave')
  }

  const getLeaveBalance = (type) => {
    const key = leaveTypes.find(lt => lt.value === type)?.value
    return leaveBalance[key] || { remaining: 0 }
  }

  const isLeaveTypeSelected = formData.leaveType && leaveTypes.find(lt => lt.value === formData.leaveType)

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ p: { xs: 1, md: 3 } }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3 }}>
          <Box sx={{ mb: 4 }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Apply for Leave
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Fill in the details to submit your leave application
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 4, mx: { xs: -2, md: -3 } }} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
            {activeStep === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Select Leave Type
                </Typography>
                <FormControl fullWidth error={!!errors.leaveType}>
                  <InputLabel>Leave Type *</InputLabel>
                  <Select
                    value={formData.leaveType}
                    label="Leave Type *"
                    onChange={(e) => handleInputChange('leaveType', e.target.value)}
                  >
                    {leaveTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography>{type.icon}</Typography>
                          <Box>
                            <Typography>{type.label}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {type.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.leaveType && (
                    <Typography variant="caption" color="error">
                      {errors.leaveType}
                    </Typography>
                  )}
                </FormControl>

                {isLeaveTypeSelected && (
                  <Alert
                    severity="info"
                    sx={{ mt: 3 }}
                    icon={<InfoIcon />}
                  >
                    <Typography variant="body2">
                      <strong>Available Balance:</strong>{' '}
                      {getLeaveBalance(formData.leaveType).remaining} days remaining
                    </Typography>
                    {formData.leaveType === 'sick' && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <WarningIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                        Medical certificate is mandatory for sick leave exceeding 2 days
                      </Typography>
                    )}
                  </Alert>
                )}
              </Box>
            )}

            {activeStep === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Set Leave Duration
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="From Date *"
                        value={formData.fromDate}
                        onChange={(newValue) => handleInputChange('fromDate', newValue)}
                        disablePast
                        slotProps={{ textField: { fullWidth: true, error: !!errors.fromDate, helperText: errors.fromDate } }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="To Date *"
                        value={formData.toDate}
                        onChange={(newValue) => handleInputChange('toDate', newValue)}
                        minDate={formData.fromDate}
                        disablePast
                        slotProps={{ textField: { fullWidth: true, error: !!errors.toDate, helperText: errors.toDate } }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <FormLabel component="legend">Duration Type *</FormLabel>
                  <RadioGroup
                    row
                    value={formData.durationType}
                    onChange={(e) => handleInputChange('durationType', e.target.value)}
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel value="full_day" control={<Radio />} label="Full Day" />
                    <FormControlLabel value="half_day" control={<Radio />} label="Half Day" />

                  </RadioGroup>
                </Box>

                {formData.durationType === 'half_day' && (
                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <TimePicker
                          label="From Time"
                          value={formData.fromTime}
                          onChange={(newValue) => handleInputChange('fromTime', newValue)}
                          slotProps={{ textField: { fullWidth: true, error: !!errors.fromTime, helperText: errors.fromTime } }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <TimePicker
                          label="To Time"
                          value={formData.toTime}
                          onChange={(newValue) => handleInputChange('toTime', newValue)}
                          slotProps={{ textField: { fullWidth: true, error: !!errors.toTime, helperText: errors.toTime } }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                )}

                {formData.totalDays > 0 && (
                  <Alert
                    severity="info"
                    sx={{ mt: 3 }}
                    icon={<CalendarIcon />}
                  >
                    <Typography variant="body2">
                      <strong>Duration:</strong> {formData.totalDays} day{formData.totalDays > 1 ? 's' : ''}
                      {formData.durationType === 'half_day' && ' (Half Day)'}
                      {formData.durationType === 'hours' && ` (${formData.totalHours} hours)`}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {activeStep === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Provide Additional Details
                </Typography>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Reason for Leave *"
                  value={formData.reason}
                  onChange={(e) => handleInputChange('reason', e.target.value)}
                  error={!!errors.reason}
                  helperText={errors.reason || 'Please provide a detailed reason for your leave'}
                  sx={{ mb: 3 }}
                />

                {formData.leaveType === 'sick' && (
                  <Box sx={{ mb: 3 }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isMedical}
                            onChange={(e) => handleInputChange('isMedical', e.target.checked)}
                          />
                        }
                        label="I have a medical certificate"
                      />
                    </FormGroup>

                    {formData.isMedical && (
                      <Box sx={{ mt: 2 }}>
                        <input
                          accept=".pdf,.jpg,.jpeg,.png"
                          style={{ display: 'none' }}
                          id="medical-certificate"
                          type="file"
                          onChange={(e) => handleFileUpload(e, 'medical')}
                        />
                        <label htmlFor="medical-certificate">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadIcon />}
                            sx={{ mr: 2 }}
                          >
                            Upload Medical Certificate
                          </Button>
                        </label>

                        {formData.medicalCertificate && (
                          <Chip
                            label={formData.medicalCertificate.name}
                            onDelete={() => handleRemoveFile(0, 'medical')}
                            deleteIcon={<DeleteIcon />}
                            sx={{ mt: 2 }}
                          />
                        )}

                        {errors.medicalCertificate && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                            {errors.medicalCertificate}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}

                <TextField
                  fullWidth
                  label="Emergency Contact Number *"
                  value={formData.emergencyContact}
                  onChange={(e) => {
                    // Allow only numbers and max 10 digits
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    handleInputChange('emergencyContact', value)
                  }}
                  error={!!errors.emergencyContact}
                  helperText={errors.emergencyContact || 'Provide a valid 10-digit contact number'}
                  sx={{ mb: 3 }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
                />

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Handover Work To</InputLabel>
                  <Select
                    value={formData.handoverTo}
                    label="Handover Work To"
                    onChange={(e) => handleInputChange('handoverTo', e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {teamMembers.map((member) => (
                      <MenuItem key={member.id} value={member.id}>
                        {member.name}{member.role ? ` - ${member.role}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Work Handover Notes"
                  value={formData.workHandoverNotes}
                  onChange={(e) => handleInputChange('workHandoverNotes', e.target.value)}
                  helperText="Briefly describe pending work and handover instructions"
                />
              </Box>
            )}

            {activeStep === 3 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Review Your Application
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Leave Details
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            <strong>Type:</strong>{' '}
                            {leaveTypes.find(lt => lt.value === formData.leaveType)?.label}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>From:</strong>{' '}
                            {formData.fromDate ? formatDate(formData.fromDate) : '-'}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>To:</strong>{' '}
                            {formData.toDate ? formatDate(formData.toDate) : '-'}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Duration:</strong> {formData.totalDays} day{formData.totalDays > 1 ? 's' : ''}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Type:</strong> {formData.durationType.replace('_', ' ')}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Additional Information
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            <strong>Reason:</strong> {formData.reason.substring(0, 50)}...
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Emergency Contact:</strong> {formData.emergencyContact || 'Not provided'}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Work Handover:</strong>{' '}
                            {formData.handoverTo ? (teamMembers.find(m => m.id === Number(formData.handoverTo))?.name || 'Unknown') : 'None'}
                          </Typography>
                          {formData.workHandoverNotes && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Handover Notes:</strong> {formData.workHandoverNotes}
                            </Typography>
                          )}
                          {formData.isMedical && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Medical Certificate:</strong> {formData.medicalCertificate ? 'Attached' : 'Not attached'}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Notification Settings
                  </Typography>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.notifyManager}
                          onChange={(e) => handleInputChange('notifyManager', e.target.checked)}
                        />
                      }
                      label="Notify Manager"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.notifyTeam}
                          onChange={(e) => handleInputChange('notifyTeam', e.target.checked)}
                        />
                      }
                      label="Notify Team"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.notifyHR}
                          onChange={(e) => handleInputChange('notifyHR', e.target.checked)}
                        />
                      }
                      label="Notify HR"
                    />
                  </FormGroup>
                </Box>

                <Alert severity="warning" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    <strong>Important:</strong> Please review all details before submitting.
                    Once submitted, changes may require approval from HR.
                  </Typography>
                </Alert>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<BackIcon />}
              >
                Back
              </Button>

              <Box sx={{ display: 'flex', gap: 2 }}>
                {activeStep < steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForward />}
                  >
                    Next
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleSaveDraft}
                      startIcon={<SaveIcon />}
                    >
                      Save Draft
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={loading}
                      startIcon={<SendIcon />}
                    >
                      {loading ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Second Row - Help & Information Section */}
          <Box sx={{ mt: 4 }}>
            <Grid>
              <Grid size={{ xs: 12, md: 4 }} mb={3}>
                <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, height: '100%', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Leave Policy
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Please review the company leave policy before applying:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Minimum Notice Period"
                        secondary="2 days for planned leaves"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Medical Certificate"
                        secondary="Required for sick leave > 2 days"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Holiday Period"
                        secondary="Maximum 10 consecutive days"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Approval Time"
                        secondary="Typically 2-3 working days"
                      />
                    </ListItem>
                  </List>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ mt: 2 }}
                    startIcon={<DownloadIcon />}
                  >
                    Download Full Policy
                  </Button>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }} mb={3}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    <CheckIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#4caf50' }} />
                    Current Leave Balance
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(leaveBalance).map(([type, data]) => (
                      <Grid size={{ xs: 6 }} key={type}>
                        <Box sx={{ p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {data.remaining} days
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Upcoming Holidays
                  </Typography>
                  {/* Added simple list for holidays since real calendar data isn't available */}
                  <List dense>
                    {upcomingHolidays.length > 0 ? (
                      upcomingHolidays.map((holiday, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={holiday.name}
                            secondary={formatDate(holiday.date, 'MMM d, yyyy')}
                          />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem>
                        <ListItemText
                          primary="No upcoming holidays"
                          secondary="Check back later"
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}

export default ApplyLeave